"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NaniumConsumerBrowserHttp = void 0;
const json_1 = require("../../serializers/json");
const http_core_1 = require("./http.core");
const objects_1 = require("../../objects");
const naniumBuffer_1 = require("../../interfaces/naniumBuffer");
const naniumStream_1 = require("../../interfaces/naniumStream");
const core_1 = require("../core");
class NaniumConsumerBrowserHttp {
    constructor(config) {
        this.activeRequests = [];
        this.config = {
            ...{
                apiUrl: '/api',
                apiEventUrl: '/events',
                onServerConnectionRestored: () => {
                },
                requestInterceptors: [],
                serializer: new json_1.NaniumJsonSerializer(),
                handleError: (response) => {
                    alert(response);
                    return Promise.resolve();
                },
                isResponsible: async () => Promise.resolve(1),
                isResponsibleForEvent: async () => Promise.resolve(1),
            },
            ...(config || {})
        };
        this.httpCore = new http_core_1.HttpCore(this.config, async (method, url, body, headers) => await this.httpRequest(method, url, body, headers));
    }
    async init() {
        if (!this.config.apiUrl.startsWith('http')) {
            this.config.apiUrl = window.location.protocol + '//' + window.location.host +
                (this.config.apiUrl.startsWith('/') ? '' : '/') + this.config.apiUrl;
        }
    }
    async terminate() {
        for (const ar of this.activeRequests) {
            try {
                ar.abort();
            }
            catch {
            }
        }
        this.activeRequests = [];
        this.httpCore.id = undefined;
        this.httpCore.terminated = true;
        this.httpCore = new http_core_1.HttpCore(this.config, async (method, url, body, headers) => await this.httpRequest(method, url, body, headers));
    }
    async isResponsible(request, serviceName) {
        return await this.config.isResponsible(request, serviceName);
    }
    async execute(serviceName, request, executionContext) {
        var _a, _b;
        // execute request interceptors
        if ((_a = this.config.requestInterceptors) === null || _a === void 0 ? void 0 : _a.length) {
            let result;
            for (const interceptor of this.config.requestInterceptors) {
                result = await (typeof interceptor === 'function' ? new interceptor() : interceptor).execute(request, executionContext !== null && executionContext !== void 0 ? executionContext : {});
                // if an interceptor returns an object other than the request it is a result and the execution shall be
                // finished with this result
                if (result !== undefined && result !== request) {
                    return result;
                }
            }
        }
        // execute the request
        let ResponseType = (0, core_1.getPrimaryResponseType)(request);
        let response;
        if (naniumStream_1.NaniumStream.isNaniumStream(ResponseType)) {
            response = await this.stream(serviceName, request);
        }
        else {
            response = await this.httpCore.sendRequest(serviceName, request);
        }
        // execute response interceptors
        if ((_b = this.config.responseInterceptors) === null || _b === void 0 ? void 0 : _b.length) {
            let responseFromInterceptor;
            for (const interceptor of this.config.responseInterceptors) {
                responseFromInterceptor = await (typeof interceptor === 'function' ? new interceptor() : interceptor).execute(request, response);
                // if an interceptor returns an object other than the original response instance, the returned value will replace the original response;
                if (responseFromInterceptor !== undefined && responseFromInterceptor !== response) {
                    return responseFromInterceptor;
                }
            }
        }
        return response;
    }
    async stream(serviceName, request) {
        var _a;
        const streamItemConstructor = (_a = request.constructor[objects_1.responseTypeSymbol]) === null || _a === void 0 ? void 0 : _a[1];
        const resultStream = new naniumStream_1.NaniumStream(streamItemConstructor);
        // transmission
        const abortController = new AbortController();
        this.activeRequests.push(abortController);
        const req = new Request(this.config.apiUrl + '?' + serviceName, {
            method: 'post',
            mode: 'cors',
            redirect: 'follow',
            body: this.config.serializer.serialize({ serviceName, request }),
            signal: abortController.signal // make the request abortable
        });
        fetch(req)
            .then((response) => response.body)
            .then((rb) => {
            const reader = rb.getReader();
            let restFromLastTime;
            let deserialized;
            return new ReadableStream({
                cancel: (reason) => {
                    resultStream.error(reason);
                    this.activeRequests = this.activeRequests.filter(r => r !== abortController);
                },
                start: (controller) => {
                    const push = () => {
                        reader.read().then(({ done, value }) => {
                            var _a, _b;
                            if (done) {
                                controller.close();
                                resultStream.end();
                                this.activeRequests = this.activeRequests.filter(r => r !== abortController);
                                return;
                            }
                            try {
                                if (naniumBuffer_1.NaniumBuffer.isNaniumBuffer((_a = request.constructor[objects_1.responseTypeSymbol]) === null || _a === void 0 ? void 0 : _a[1])) {
                                    resultStream.write(naniumBuffer_1.NaniumBuffer.isNaniumBuffer(value) ? value : new naniumBuffer_1.NaniumBuffer(value));
                                }
                                else {
                                    deserialized = this.config.serializer.deserializePartial(value, restFromLastTime);
                                    if ((_b = deserialized.data) === null || _b === void 0 ? void 0 : _b.length) {
                                        for (const data of deserialized.data) {
                                            resultStream.write(objects_1.NaniumObject.create(data, streamItemConstructor, request.constructor[objects_1.genericTypesSymbol]));
                                        }
                                    }
                                    restFromLastTime = deserialized.rest;
                                }
                            }
                            catch (e) {
                                this.activeRequests = this.activeRequests.filter(r => r !== abortController);
                                controller.close();
                                resultStream.error(e);
                            }
                            // read next portion from stream
                            push();
                        });
                    };
                    // start reading from stream
                    push();
                },
            });
        });
        return resultStream;
    }
    async subscribe(eventNameOrConstructor, handler, context) {
        return await this.httpCore.subscribe(eventNameOrConstructor, handler, context);
    }
    async unsubscribe(subscription, eventName) {
        await this.httpCore.unsubscribe(subscription, eventName);
    }
    async removeClient(_clientId) {
    }
    emit(_eventName, _event, _context) {
    }
    async isResponsibleForEvent(eventName, context) {
        return await this.config.isResponsibleForEvent(eventName, context);
    }
    receiveSubscription(subscriptionData) {
        throw new Error('not implemented');
    }
    async httpRequest(method, url, body, headers) {
        return new Promise((resolve, reject) => {
            // transmission
            const abortController = new AbortController();
            this.activeRequests.push(abortController);
            const req = new Request(url, {
                method: method,
                body: body,
                headers: headers,
                signal: abortController.signal // make the request abortable
            });
            fetch(req)
                .then(async (response) => {
                const data = await response.arrayBuffer();
                if (response.ok) {
                    resolve(data);
                }
                else {
                    reject(data);
                }
            })
                .catch((error) => {
                reject(error);
            })
                .finally(() => {
                this.activeRequests = this.activeRequests.filter(r => r !== abortController);
            });
        });
    }
}
exports.NaniumConsumerBrowserHttp = NaniumConsumerBrowserHttp;
//# sourceMappingURL=browserHttp.js.map